import { useEffect, useState } from "react";
import { parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import {
    PresenteeismConciliationDetailService,
    PresenteeismConciliationDetailServiceModel,
} from "@services/PresenteeismConciliationDetailService";
import { DashboardService } from "@services/DashboardService";
import { PresenteeismConciliationDetailRowRequest } from "./PresenteeismConciliationDetailModalViewModel";

export interface PendingCheckoutRowRequest extends PresenteeismConciliationDetailRowRequest {
    photo?: string;
    edificioDisplay?: string;
    fechaDisplay?: string;
}

interface UsePresenteeismManualCheckoutModalViewModelParams {
    isOpen: boolean;
    request: PendingCheckoutRowRequest | null;
    service: PresenteeismConciliationDetailService;
    dashboardService: DashboardService;
    timezone: string;
}

const normalizeTime = (time: string): string => {
    const [hour, minute] = time.split(":");
    const normalizedHour = parseInt(hour, 10).toString();
    const normalizedMinute = parseInt(minute, 10).toString();
    return `${normalizedHour}:${normalizedMinute.length === 1 ? `0${normalizedMinute}` : normalizedMinute}`;
};

const isValidTime = (time: string): boolean => {
    if (!time || !time.includes(":")) {
        return false;
    }
    const [hour, minute] = time.split(":");
    const parsedHour = Number(hour);
    const parsedMinute = Number(minute);
    return Number.isFinite(parsedHour) && Number.isFinite(parsedMinute)
        && parsedHour >= 0 && parsedHour <= 23
        && parsedMinute >= 0 && parsedMinute <= 59;
};

const buildTimestampForDayAndTime = (day: string, time: string, timezone: string): number => {
    const combinedDateTimeString = `${day}T${normalizeTime(time)}:00`;
    const zonedDate = zonedTimeToUtc(
        parse(combinedDateTimeString, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
        timezone
    );
    return zonedDate.getTime();
};

export const usePresenteeismManualCheckoutModalViewModel = ({
    isOpen,
    request,
    service,
    dashboardService,
    timezone,
}: UsePresenteeismManualCheckoutModalViewModelParams) => {
    const [detail, setDetail] = useState<PresenteeismConciliationDetailServiceModel.PendingCheckoutDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hour, setHour] = useState("");
    const [hourWarning, setHourWarning] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !request) {
            setDetail(null);
            setLoading(false);
            setError(null);
            setHour("");
            setHourWarning(null);
            setSaving(false);
            setSaveError(null);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setSaveError(null);
        setHourWarning(null);
        setHour("");

        service.getPendingCheckoutDetail({
            ...request,
            signal: controller.signal,
        })
            .then((result) => {
                setDetail(result);
                const shiftHourTo = (result.checkinRecord?.shift as { hourTo?: string } | undefined)?.hourTo;
                if (result.hasShift && shiftHourTo) {
                    setHour(shiftHourTo);
                }
            })
            .catch((fetchError) => {
                if (controller.signal.aborted) {
                    return;
                }

                console.error(fetchError);
                setDetail(null);
                setError("No se pudo cargar la información de este registro.".localized());
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => {
            controller.abort();
        };
    }, [isOpen, request, service]);

    const qpID = detail?.checkinQontactPoint?.id;
    const canResolveBuilding = Boolean(qpID);
    const canSubmit = !saving && !loading;

    const handleHourValidityChange = (isValid: boolean) => {
        setHourWarning(isValid ? null : "Ese no es un horario válido.".localized());
    };

    // committedHour: el valor tal cual se leyó del campo en el instante del click en
    // Guardar (ver PresenteeismManualCheckoutModal.tsx). Se usa ese valor en vez del
    // estado `hour` para no depender de si ya se terminó de procesar el blur del campo.
    const handleSave = async (committedHour: string): Promise<boolean> => {
        const effectiveHour = committedHour || hour;

        if (!isValidTime(effectiveHour)) {
            // No se repite el mensaje acá: el aviso de horario inválido ya se muestra
            // en línea (hourWarning) apenas se detecta el valor incorrecto.
            return false;
        }

        if (!request || !qpID) {
            setSaveError("No pudimos identificar el edificio de este registro. Usá \"Fichada manual\" para completarlo.".localized());
            return false;
        }

        setSaving(true);
        setSaveError(null);

        try {
            const timestamp = buildTimestampForDayAndTime(request.day, effectiveHour, timezone);

            await dashboardService.createManualRecord({
                qpID,
                userID: request.requesterID,
                timestamp,
                type: "CHECK-OUT",
            });

            return true;
        } catch (saveErrorRaised) {
            console.error(saveErrorRaised);
            setSaveError("Hubo un error al intentar registrar la salida.".localized());
            return false;
        } finally {
            setSaving(false);
        }
    };

    const buildingName = detail?.checkinBuilding?.name
        || detail?.checkinBuilding?.buildingName
        || detail?.checkinBuilding?.title
        || request?.edificioDisplay;

    return {
        detail,
        loading,
        error,
        hour,
        setHour,
        hourWarning,
        handleHourValidityChange,
        saving,
        saveError,
        canResolveBuilding,
        canSubmit,
        handleSave,
        buildingName,
    };
};
